import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { ShareLinkMode } from 'apiClient/Api';

/** Vom Dialog gesammelte Eingaben für „Neue Einladung versenden". */
export interface SendInvitationValues {
    name: string;
    customerEmail: string;
    customerName: string;
    customerReference: string;
    mode: ShareLinkMode;
    expiresAt: string | null;
}

/** Einmalig zurückgelieferte Zugangsdaten (serverseitig untypisiert – vgl. LinksDialog). */
export interface CreatedShareLink {
    link?: { customerEmail?: string };
    url?: string;
    token?: string;
    password?: string;
    emailSent?: boolean;
}

/** Baut den Konfigurator-Link auf dem aktuellen Host (dev/prod), sonst Fallback auf die Server-URL. */
const configuratorUrl = (created: CreatedShareLink): string | undefined =>
    created.token ? `${window.location.origin}/konfigurator/${created.token}` : created.url;

const copyText = async (text: string) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return;
        }
    } catch {
        /* Fallback unten */
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
        document.execCommand('copy');
    } catch {
        /* ignore */
    }
    document.body.removeChild(ta);
};

/**
 * „Neue Einladung versenden" in einem Schritt: erfasst Kundendaten, speichert das aktuelle
 * Projekt automatisch (Anlage/Update übernimmt `onSend`) und erstellt + verschickt den Link.
 */
export const SendInvitationDialog: React.FC<{
    open: boolean;
    defaultName?: string;
    defaultCustomerName?: string;
    defaultCustomerReference?: string;
    /** Optionaler Hinweistext (überschreibt den Standard "aktuelle Konfiguration wird gespeichert"). */
    hint?: string;
    /** Speichert das Projekt und erstellt den Link; liefert die einmaligen Zugangsdaten (oder null bei Fehler). */
    onSend: (values: SendInvitationValues) => Promise<CreatedShareLink | null>;
    onClose: () => void;
}> = ({ open, defaultName, defaultCustomerName, defaultCustomerReference, hint, onSend, onClose }) => {
    const { t } = useTranslation();
    const [values, setValues] = useState<SendInvitationValues>({
        name: '',
        customerEmail: '',
        customerName: '',
        customerReference: '',
        mode: ShareLinkMode.Edit,
        expiresAt: null,
    });
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState<CreatedShareLink | null>(null);

    useEffect(() => {
        if (open) {
            setValues({
                name: defaultName || '',
                customerEmail: '',
                customerName: defaultCustomerName || '',
                customerReference: defaultCustomerReference || '',
                mode: ShareLinkMode.Edit,
                expiresAt: null,
            });
            setCreated(null);
            setBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const patch = (p: Partial<SendInvitationValues>) => setValues((v) => ({ ...v, ...p }));

    const submit = async () => {
        if (!values.customerEmail.trim()) return;
        setBusy(true);
        try {
            const result = await onSend({ ...values, customerEmail: values.customerEmail.trim() });
            if (result) setCreated(result);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('calculation.invite.title', 'Neue Einladung versenden')}</DialogTitle>
            <DialogContent dividers>
                {!created ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {hint ??
                                t(
                                    'calculation.invite.hint',
                                    'Die aktuelle Konfiguration wird automatisch gespeichert und dem Kunden per E-Mail als Link zugeschickt.',
                                )}
                        </Typography>
                        <TextField
                            label={t('calculation.store.name')}
                            size="small"
                            value={values.name}
                            onChange={(e) => patch({ name: e.target.value })}
                        />
                        <TextField
                            label={t('calculation.invite.customerEmail', 'Kunden-E-Mail')}
                            size="small"
                            type="email"
                            required
                            autoFocus
                            value={values.customerEmail}
                            onChange={(e) => patch({ customerEmail: e.target.value })}
                            error={!values.customerEmail.trim()}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label={t('calculation.store.customerName')}
                                size="small"
                                fullWidth
                                value={values.customerName}
                                onChange={(e) => patch({ customerName: e.target.value })}
                            />
                            <TextField
                                label={t('calculation.store.customerReference')}
                                size="small"
                                value={values.customerReference}
                                onChange={(e) => patch({ customerReference: e.target.value })}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                select
                                label={t('calculation.invite.mode', 'Modus')}
                                size="small"
                                value={values.mode}
                                onChange={(e) => patch({ mode: e.target.value as ShareLinkMode })}
                                sx={{ width: 180 }}
                            >
                                <MenuItem value={ShareLinkMode.Edit}>
                                    {t('calculation.invite.modeEdit', 'Bearbeiten')}
                                </MenuItem>
                                <MenuItem value={ShareLinkMode.View}>
                                    {t('calculation.invite.modeView', 'Nur ansehen')}
                                </MenuItem>
                            </TextField>
                            <TextField
                                label={t('calculation.invite.expires', 'Ablauf (optional)')}
                                size="small"
                                type="date"
                                value={values.expiresAt ? values.expiresAt.slice(0, 10) : ''}
                                onChange={(e) =>
                                    patch({ expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })
                                }
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Stack>
                    </Stack>
                ) : (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {created.emailSent
                                ? t('calculation.invite.sent', {
                                      email: created.link?.customerEmail,
                                      defaultValue: 'Einladung an {{email}} gesendet.',
                                  })
                                : t(
                                      'calculation.invite.sentNoMail',
                                      'Link erstellt – der E-Mail-Versand ist fehlgeschlagen. Bitte die Zugangsdaten manuell weitergeben:',
                                  )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                            {t('calculation.invite.secretsOnce', 'Diese Zugangsdaten werden nur einmal angezeigt.')}
                        </Typography>
                        <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {configuratorUrl(created)}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const u = configuratorUrl(created);
                                        if (u) copyText(u);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </IconButton>
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2">
                                    {t('calculation.invite.password', 'Passwort')}:{' '}
                                    <b style={{ fontFamily: 'monospace' }}>{created.password}</b>
                                </Typography>
                                <IconButton size="small" onClick={() => created.password && copyText(created.password)}>
                                    <FontAwesomeIcon icon={faCopy} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {created ? t('common.close', 'Schließen') : t('common.cancel', 'Abbrechen')}
                </Button>
                {!created && (
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                        onClick={submit}
                        disabled={busy || !values.customerEmail.trim()}
                    >
                        {t('calculation.invite.send', 'Speichern & Versenden')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
